import React, { useState, useRef } from "react";
import { motion } from "framer-motion";

export default function BeforeAfter() {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const handleMove = (clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  return (
    <section
      id="transformation"
      data-testid="before-after-section"
      className="relative section-pad"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="text-[#27C6FF] uppercase tracking-[0.3em] text-xs mb-5 font-heading">
            Before / After
          </div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
            From depleted to
            <br />
            <span className="gradient-text">glowing — sustainably.</span>
          </h2>
          <p className="mt-6 text-[#D8CCFF] text-base md:text-lg font-body max-w-2xl">
            We don&rsquo;t chase dramatic before/after shots. We chase the
            transformation you can actually keep — energy, mood, lab markers,
            sleep, and the way you show up for your life.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="mt-12 relative rounded-[28px] overflow-hidden gradient-border glass"
          ref={ref}
          onMouseMove={(e) => handleMove(e.clientX)}
          onTouchMove={(e) => handleMove(e.touches[0].clientX)}
          data-testid="before-after-slider"
        >
          <div className="relative aspect-[16/9]">
            <img
              src="/generated/before_after.png"
              alt="DietDotIn client wellness transformation — energy, sleep and lifestyle change"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <img
              src="/generated/before_after.png"
              alt="Before transformation"
              className="absolute inset-0 w-full h-full object-cover grayscale brightness-75"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
            />

            <div className="absolute top-5 left-5 glass-strong px-3 py-1.5 rounded-full text-xs text-[#CFAEFF] font-heading">
              Before
            </div>
            <div className="absolute top-5 right-5 glass-strong px-3 py-1.5 rounded-full text-xs text-[#27C6FF] font-heading aqua-glow">
              After
            </div>

            <div
              className="absolute top-0 bottom-0 w-px bg-gradient-to-b from-[#7A2BFF] via-[#27C6FF] to-[#CFAEFF] shadow-[0_0_24px_rgba(39,198,255,0.85)]"
              style={{ left: `${pos}%` }}
            >
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-gradient-to-br from-[#7A2BFF] to-[#27C6FF] shadow-[0_0_30px_rgba(122,43,255,0.7)] flex items-center justify-center text-white font-bold">
                ⇆
              </div>
            </div>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={pos}
            onChange={(e) => setPos(parseInt(e.target.value, 10))}
            data-testid="before-after-slider-input"
            className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
            aria-label="Drag to compare before and after"
          />
        </motion.div>
      </div>
    </section>
  );
}
